import type { ScopeSchema, ScopeSectionSchema, ScopeFieldSchema, ScopeGroupSchema } from './types'
import { generalSections } from './general'
import { kycSections } from './kyc'
import { amlSections } from './aml'
import { biometricsSections } from './biometrics'
import { kybSections } from './kyb'
import { recipesSections } from './recipes'
import { rulesSections } from './rules'
import { sowSections } from './sow'
import type { ScopeData } from '../types'

const TRI_STATE_OPTIONS = [
  { value: 'unknown', label: "Don't Know" },
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
] as const

const BOOLEAN_OPTIONS = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
] as const

const normalizeTriStateOptions = (sections: ScopeSectionSchema[]): ScopeSectionSchema[] => {
  return sections.map((s) => ({
    ...s,
    groups: s.groups,
    fields: s.fields.map((f) => {
      if (f.valueType !== 'tri_state') return f
      if (f.options && f.options.length > 0) return f
      return { ...f, options: [...TRI_STATE_OPTIONS] }
    }),
  }))
}

const normalizeBooleanOptions = (sections: ScopeSectionSchema[]): ScopeSectionSchema[] => {
  return sections.map((s) => ({
    ...s,
    groups: s.groups,
    fields: s.fields.map((f) => {
      if (f.valueType !== 'boolean') return f
      if (f.options && f.options.length > 0) return f
      return { ...f, options: [...BOOLEAN_OPTIONS] }
    }),
  }))
}

export const scopeSchema: ScopeSchema = {
  version: 1,
  sections: normalizeBooleanOptions(
    normalizeTriStateOptions([
    ...generalSections,
    ...kycSections,
    ...amlSections,
    ...biometricsSections,
    ...kybSections,
    ...recipesSections,
    ...rulesSections,
    ...sowSections,
    ])
  ),
}

import { readSchemaOverridesSync } from './overridesCache'

type SchemaViewerOverridesByKey = Record<string, Record<string, string>>

const readSchemaViewerOverrides = (): SchemaViewerOverridesByKey => readSchemaOverridesSync()

const applyOverrides = <T extends Record<string, unknown>>(rowType: 'section' | 'group' | 'field', id: string, base: T): T => {
  const overrides = readSchemaViewerOverrides()
  const key = `${rowType}:${id}`
  const o = overrides[key]
  if (!o) return base

  // Only allow known schema metadata props to be overridden.
  const next: T = { ...base }
  const allowed: Array<keyof T & string> = [
    'formTitle',
    'formLabel',
    'formDescription',
    'tooltip',
    'placeholder',
    'aiContext',
    'pdfTitle',
    'pdfLabel',
    'pdfDescription',
  ]

  for (const k of allowed) {
    const v = o[k]
    if (typeof v === 'string') {
      ;(next as any)[k] = v
    }
  }

  return next
}

export const getSectionById = (sectionId: string): ScopeSectionSchema | undefined =>
  (() => {
    const s = scopeSchema.sections.find((x) => x.id === sectionId)
    return s ? applyOverrides('section', s.id, s) : undefined
  })()

export const getFieldById = (fieldId: string): ScopeFieldSchema | undefined =>
  (() => {
    const f = scopeSchema.sections.flatMap((s) => s.fields).find((x) => x.id === fieldId)
    return f ? applyOverrides('field', f.id, f) : undefined
  })()

export const getGroupById = (groupId: string): ScopeGroupSchema | undefined =>
  (() => {
    const g = scopeSchema.sections.flatMap((s) => s.groups || []).find((x) => x.id === groupId)
    return g ? applyOverrides('group', g.id, g) : undefined
  })()

const readByPath = (obj: unknown, path: string): unknown => {
  if (!obj) return undefined
  const parts = path.split('.').filter(Boolean)
  let current: unknown = obj

  for (const part of parts) {
    if (!current || typeof current !== 'object') return undefined
    current = (current as Record<string, unknown>)[part]
  }

  return current
}

export const getValueByPath = (scope: ScopeData, path: string): unknown => readByPath(scope, path)

const isRuleSatisfied = (value: unknown, rule: { equals?: unknown; notEquals?: unknown; in?: unknown[]; notIn?: unknown[] }) => {
  if (Object.prototype.hasOwnProperty.call(rule, 'equals') && value !== rule.equals) return false
  if (Object.prototype.hasOwnProperty.call(rule, 'notEquals') && value === rule.notEquals) return false
  if (rule.in && !rule.in.includes(value)) return false
  if (rule.notIn && rule.notIn.includes(value)) return false
  return true
}

export const isFieldVisible = (field: ScopeFieldSchema, scope: ScopeData): boolean => {
  const rules = field.visibleWhen
  if (!rules || rules.length === 0) return true

  return rules.every((r) => {
    const v = getValueByPath(scope, r.dependsOn)
    return isRuleSatisfied(v, r)
  })
}

export const isGroupVisible = (group: ScopeGroupSchema, scope: ScopeData): boolean => {
  const rules = group.visibleWhen
  if (!rules || rules.length === 0) return true

  return rules.every((r) => {
    const v = getValueByPath(scope, r.dependsOn)
    return isRuleSatisfied(v, r)
  })
}
